import { supabaseClient } from '~/shared/common/supabase/client'
import {
  CreateConsentsRequest,
  CreateConsentsResponse,
  GetActiveAgreementsResponse,
  GetMissingConsentsResponse,
} from '~/shared/agreement/agreement.api.schema'
import {
  agreementVersionFromWire,
  type AgreementType,
  type AgreementVersion,
} from '~/shared/agreement/agreement.supabase.schema'

async function bearerHeader(): Promise<Record<string, string>> {
  const { data } = await supabaseClient.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Not authenticated')
  return { Authorization: `Bearer ${token}` }
}

export async function fetchActiveAgreements(): Promise<AgreementVersion[]> {
  const res = await fetch('/api/agreement/active')
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? `Failed to load active agreements (${res.status})`)
  }
  const json = GetActiveAgreementsResponse.parse(await res.json())
  return json.data.map(agreementVersionFromWire)
}

export async function fetchMissingConsents(): Promise<AgreementType[]> {
  const headers = await bearerHeader()
  const res = await fetch('/api/agreement/missing', { headers })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? `Failed to load missing consents (${res.status})`)
  }
  const json = GetMissingConsentsResponse.parse(await res.json())
  return json.data
}

export async function postConsents(input: CreateConsentsRequest): Promise<CreateConsentsResponse> {
  const headers = await bearerHeader()
  const body = CreateConsentsRequest.parse(input)
  const res = await fetch('/api/agreement/consent', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(errBody.error ?? `Failed to record consent (${res.status})`)
  }
  return CreateConsentsResponse.parse(await res.json())
}
